<template>
  <div class="flex-1 flex flex-col min-h-0">
    <div class="flex items-center justify-between mb-4">
      <h4 class="text-lg font-medium text-gray-800">Teams in League ({{ teams?.length || 0 }})</h4>
    </div>
    
    <!-- Three Column Layout -->
    <div class="flex-1 flex gap-4 min-h-0">
      <!-- Left Column - Teams Search (Small) -->
      <div class="w-64 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
        <div class="flex items-center mb-3">
          <i class="fas fa-search text-pink-500 mr-2"></i>
          <span class="font-semibold text-gray-700">Search Teams</span>
        </div>
        
        <!-- Search Input -->
        <div class="mb-4">
          <input
            :value="addTeamSearch"
            @input="$emit('update:addTeamSearch', $event.target.value); $emit('search-teams')"
            type="text"
            placeholder="Search teams..."
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-sm"
          />
        </div>
        
        <!-- Search Results -->
        <div class="flex-1 overflow-auto min-h-0">
          <div v-if="isSearchingAddTeam" class="text-center text-gray-500 py-4">
            <i class="fas fa-spinner fa-spin mr-2"></i>
            Searching...
          </div>
          
          <div v-else-if="addTeamResults.length > 0" class="space-y-2">
            <div class="text-sm font-medium text-gray-700 mb-2">
              Found {{ addTeamResults.length }} teams
            </div>
            <div
              v-for="team in addTeamResults"
              :key="team.id"
              class="bg-gray-50 border border-gray-200 rounded-lg p-2 hover:bg-gray-100 transition-colors"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center space-x-2">
                    <i class="fas fa-users text-gray-400 text-xs"></i>
                    <span class="text-sm font-medium text-gray-800 truncate">{{ team.team_name }}</span>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    <span v-if="team.region">{{ team.region }}</span>
                    <span v-if="team.region && team.league"> • </span>
                    <span v-if="team.league">{{ team.league }}</span>
                  </div>
                </div>
                <button
                  @click="$emit('add-team', team)"
                  class="ml-2 px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded hover:bg-pink-200 transition-colors flex-shrink-0"
                  title="Add to league"
                >
                  <i class="fas fa-plus"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div v-else-if="addTeamSearch && !isSearchingAddTeam" class="text-center text-gray-500 py-4">
            <i class="fas fa-search text-2xl mb-2"></i>
            <p class="text-sm">No teams found</p>
          </div>
          
          <div v-else class="text-center text-gray-500 py-4">
            <i class="fas fa-search text-2xl mb-2"></i>
            <p class="text-sm">Search for teams</p>
            <p class="text-xs">Enter a team name to add to the league</p>
          </div>
        </div>
      </div>

      <!-- Middle Column - Active Teams -->
      <div class="flex-1 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
        <div class="flex items-center mb-2">
          <i class="fas fa-users text-pink-500 mr-2"></i>
          <span class="font-semibold text-gray-700">Current League Teams</span>
        </div>
        <ul v-if="teams && teams.length" class="space-y-2 flex-1 overflow-auto min-h-0">
          <li 
            v-for="teamItem in teams" 
            :key="teamItem.id" 
            class="bg-gray-50 rounded transition-colors"
            :class="{ 'ring-2 ring-pink-500 bg-pink-50': selectedTeam?.id === teamItem.id }"
          >
            <!-- Team Item -->
            <div 
              @click="selectTeam(teamItem)"
              class="flex items-center justify-between px-2 py-1 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <div class="flex items-center space-x-2">
                <i class="fas fa-users text-gray-400"></i>
                <div>
                  <span class="font-medium">{{ teamItem.team?.team_name || 'Unknown Team' }}</span>
                  <div class="text-xs text-gray-500">
                    <span v-if="teamItem.team?.region">{{ teamItem.team.region }}</span>
                    <span v-if="teamItem.team?.region && teamItem.team?.league"> • </span>
                    <span v-if="teamItem.team?.league">{{ teamItem.team.league }}</span>
                    <span class="ml-2 text-gray-400">ID: {{ teamItem.team_id }}</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  @click.stop="$emit('remove-team', teamItem)"
                  class="text-sm px-2 py-1 rounded flex items-center justify-center transition-all duration-150 group bg-transparent"
                  title="Remove from league"
                >
                  <i class="fas fa-trash-alt text-red-400 opacity-30 grayscale group-hover:opacity-100 group-hover:grayscale-0 group-hover:text-red-600 transition-all duration-150"></i>
                </button>
              </div>
            </div>
          </li>
        </ul>
        <div v-else class="text-gray-400 italic flex-1">No teams in league.</div>
      </div>

      <!-- Right Column - Projections Stats Mapping -->
      <div v-if="selectedTeam" class="w-1/3 bg-white rounded-xs shadow border border-gray-100 p-4 flex flex-col min-h-0">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center">
            <i class="fas fa-chart-line text-pink-500 mr-2"></i>
            <span class="font-semibold text-gray-700">Stats Mapping</span>
          </div>
          <button
            @click="selectedTeam = null"
            class="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close mapping panel"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Selected Team Info -->
        <div class="bg-pink-50 border border-pink-200 rounded-lg p-3 mb-4">
          <div class="flex items-center space-x-2">
            <i class="fas fa-users text-pink-500"></i>
            <div>
              <span class="font-medium text-pink-800 text-sm">{{ selectedTeam.team?.team_name || 'Unknown Team' }}</span>
              <div class="text-xs text-pink-600">
                <span v-if="selectedTeam.team?.region">{{ selectedTeam.team.region }}</span>
                <span v-if="selectedTeam.team?.region && selectedTeam.team?.league"> • </span>
                <span v-if="selectedTeam.team?.league">{{ selectedTeam.team.league }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Search Section -->
        <div class="mb-4">
          <div class="flex items-center mb-2">
            <i class="fas fa-search text-pink-500 mr-2"></i>
            <span class="font-semibold text-gray-700 text-sm">Search Stats</span>
          </div>
          <input
            v-model="projectionsSearchQuery"
            @input="searchProjectionsStats"
            type="text"
            placeholder="Search stats..."
            class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          />
        </div>
        
        <!-- Search Results -->
        <div class="flex-1 overflow-auto min-h-0">
          <div v-if="isSearchingProjections" class="text-center text-gray-500 py-4">
            <i class="fas fa-spinner fa-spin mr-2"></i>
            <span class="text-sm">Searching...</span>
          </div>
          
          <div v-else-if="projectionsSearchResults.length > 0" class="space-y-2">
            <div class="text-xs font-medium text-gray-700 mb-2">
              Found {{ projectionsSearchResults.length }} results
            </div>
            
            <!-- Two Column Layout for Team Totals vs Opponent Totals -->
            <div class="flex gap-2">
              <!-- Team Totals Column -->
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-600 mb-2 flex items-center">
                  <i class="fas fa-users text-pink-500 mr-1"></i>
                  Team Totals
                </div>
                <div class="space-y-2">
                  <div
                    v-for="projectionTeam in teamTotalsResults"
                    :key="projectionTeam.id"
                    class="bg-gray-50 border border-gray-200 rounded p-2 hover:bg-gray-100 transition-colors"
                  >
                    <div class="space-y-2">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                          <i class="fas fa-chart-bar text-pink-400 text-xs"></i>
                          <span class="text-sm font-medium text-gray-800">{{ projectionTeam.team_name }}</span>
                        </div>
                        <span class="text-xs px-1 py-0.5 bg-pink-100 text-pink-700 rounded">{{ formatDataType(projectionTeam.data_type) }}</span>
                      </div>
                      <div class="text-xs text-gray-600 space-y-1">
                        <div><span class="font-medium">League:</span> {{ projectionTeam.league }}</div>
                        <div v-if="selectedTeam && projectionTeam.ppg">
                          <span class="font-medium">PPG:</span> 
                          <span class="text-blue-600">{{ projectionTeam.ppg }}</span> / 
                          <span class="text-gray-500">League Avg: {{ calculateLeagueAverage(projectionTeam.data_type) }}</span>
                          <div>
                            <span class="text-gray-500">Divide PPG by League Avg:</span>
                            <span class="text-green-600">
                              {{ (Number(projectionTeam.ppg) && Number(calculateLeagueAverage(projectionTeam.data_type))) 
                              ? (Number(projectionTeam.ppg) / Number(calculateLeagueAverage(projectionTeam.data_type))).toFixed(2) 
                              : 'N/A' }} | {{ ((Number(projectionTeam.ppg) / Number(calculateLeagueAverage(projectionTeam.data_type))).toFixed(2)*100).toFixed(2) }}%
                          </span>
                          </div>
                        </div>
                      </div>
                      <div class="flex space-x-2">
                        <button
                          @click="addProjectionTeamToLeague(projectionTeam, selectedTeam)"
                          class="flex-1 px-2 py-1 bg-pink-600 text-white text-xs rounded hover:bg-pink-700 transition-colors"
                          title="Add this projections team to the league"
                        >
                          <i class="fas fa-plus mr-1"></i>
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Empty state for Team Totals -->
                  <div v-if="teamTotalsResults.length === 0" class="text-center text-gray-400 py-4">
                    <i class="fas fa-users text-xl mb-2"></i>
                    <p class="text-xs">No team totals found</p>
                  </div>
                </div>
              </div>
              
              <!-- Opponent Totals Column -->
              <div class="flex-1">
                <div class="text-xs font-medium text-gray-600 mb-2 flex items-center">
                  <i class="fas fa-shield-alt text-pink-500 mr-1"></i>
                  Opponent Totals
                </div>
                <div class="space-y-2">
                  <div
                    v-for="projectionTeam in opponentTotalsResults"
                    :key="projectionTeam.id"
                    class="bg-gray-50 border border-gray-200 rounded p-2 hover:bg-gray-100 transition-colors"
                  >
                    <div class="space-y-2">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                          <i class="fas fa-chart-bar text-pink-400 text-xs"></i>
                          <span class="text-sm font-medium text-gray-800">{{ projectionTeam.team_name }}</span>
                        </div>
                        <span class="text-xs px-1 py-0.5 bg-pink-100 text-pink-700 rounded">{{ formatDataType(projectionTeam.data_type) }}</span>
                      </div>
                      <div class="text-xs text-gray-600 space-y-1">
                        <div><span class="font-medium">League:</span> {{ projectionTeam.league }}</div>
                        <div v-if="selectedTeam && projectionTeam.ppg">
                          <span class="font-medium">PPG:</span> 
                          <span class="text-blue-600">{{ projectionTeam.ppg }}</span> / 
                          <span class="text-gray-500">League Avg: {{ calculateLeagueAverage(projectionTeam.data_type) }}</span>
                          <div>
                          <span class="text-gray-500">Divide PPG by League Avg:</span>
                          <span class="text-green-600">
                            {{ (Number(projectionTeam.ppg) && Number(calculateLeagueAverage(projectionTeam.data_type))) 
                              ? (Number(projectionTeam.ppg) / Number(calculateLeagueAverage(projectionTeam.data_type))).toFixed(2) 
                              : 'N/A' }} | {{ ((Number(projectionTeam.ppg) / Number(calculateLeagueAverage(projectionTeam.data_type))).toFixed(2)*100).toFixed(2) }}%
                          </span>
                          </div>
                        </div>
                      </div>
                      <div class="flex space-x-2">
                        <button
                          @click="addProjectionTeamToLeague(projectionTeam, selectedTeam)"
                          class="flex-1 px-2 py-1 bg-pink-600 text-white text-xs rounded hover:bg-pink-700 transition-colors"
                          title="Add this projections team to the league"
                        >
                          <i class="fas fa-plus mr-1"></i>
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Empty state for Opponent Totals -->
                  <div v-if="opponentTotalsResults.length === 0" class="text-center text-gray-400 py-4">
                    <i class="fas fa-shield-alt text-xl mb-2"></i>
                    <p class="text-xs">No opponent totals found</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else-if="projectionsSearchQuery && !isSearchingProjections" class="text-center text-gray-500 py-4">
            <i class="fas fa-search text-xl mb-2"></i>
            <p class="text-sm">No projections found</p>
          </div>
          
          <div v-else class="text-center text-gray-500 py-4">
            <i class="fas fa-chart-line text-xl mb-2"></i>
            <p class="text-sm">Search for stats</p>
            <p class="text-xs">Enter a team name to find matching stats data</p>
          </div>
        </div>
      </div>

      <!-- Empty Right Panel -->
      <div v-else class="w-80 bg-gray-50 rounded-xs border border-gray-200 flex items-center justify-center">
        <div class="text-center text-gray-500">
          <i class="fas fa-mouse-pointer text-2xl mb-2"></i>
          <p class="text-sm">Select a team to add stats</p>
          <p class="text-xs">Click on any team from the middle panel</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeagueTeamsDisplay',
  props: {
    teams: {
      type: Array,
      default: () => []
    },
    showAddTeamSearch: {
      type: Boolean,
      default: false
    },
    addTeamSearch: {
      type: String,
      default: ''
    },
    addTeamResults: {
      type: Array,
      default: () => []
    },
    isSearchingAddTeam: {
      type: Boolean,
      default: false
    },
    projectionsStats: {
      type: Array,
      default: () => []
    },
    filteredProjectionsStats: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedTeam: null,
      projectionsSearchQuery: '',
      projectionsSearchResults: [],
      isSearchingProjections: false
    }
  },
  computed: {
    teamTotalsResults() {
      return this.projectionsSearchResults.filter(result => 
        result.data_type === 'team_totals'
      )
    },
    opponentTotalsResults() {
      return this.projectionsSearchResults.filter(result => 
        result.data_type === 'opponent_totals'
      )
    }
  },
  emits: ['toggle-add-team', 'search-teams', 'clear-add-team-search', 'add-team', 'remove-team', 'update:addTeamSearch', 'add-projection-team'],
  methods: {
    selectTeam(team) {
      this.selectedTeam = team
      this.projectionsSearchQuery = ''
      this.projectionsSearchResults = []
    },
    searchProjectionsStats() {
      const searchTerm = this.projectionsSearchQuery
      if (!searchTerm || searchTerm.length < 2) {
        this.projectionsSearchResults = []
        return
      }
      
      this.isSearchingProjections = true
      
      // Simulate API call delay
      setTimeout(() => {
        // Use filteredProjectionsStats instead of projectionsStats to match the table
        const dataSource = this.filteredProjectionsStats.length > 0 ? this.filteredProjectionsStats : this.projectionsStats
        
        const results = dataSource.filter(stat => 
          stat.team_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stat.league.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 10) // Limit to 10 results
        
        this.projectionsSearchResults = results
        this.isSearchingProjections = false
      }, 300)
    },

    addProjectionTeamToLeague(projectionTeam, currentTeam) {
      // Emit event to parent to handle adding projection team
      this.$emit('add-projection-team', {
        projectionTeam,
        currentTeam,
        dataType: projectionTeam.data_type,
        leagueAverage: this.calculateLeagueAverage(projectionTeam.data_type),
        dividePPGByLeagueAverage: (Number(projectionTeam.ppg) && Number(this.calculateLeagueAverage(projectionTeam.data_type))) 
                              ? (Number(projectionTeam.ppg) / Number(this.calculateLeagueAverage(projectionTeam.data_type))).toFixed(2) 
                              : 'N/A' 
      })
    },
    formatDataType(dataType) {
      return dataType.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    },
    
    calculateLeagueAverage(dataType) {
      // Use filteredProjectionsStats instead of projectionsStats to match the table
      const dataSource = this.filteredProjectionsStats.length > 0 ? this.filteredProjectionsStats : this.projectionsStats
      
      if (!dataSource || dataSource.length === 0) {
        return 'N/A'
      }
      
      // Calculate average PPG from data matching the specific data_type
      const filteredData = dataSource.filter(stat => stat.data_type === dataType)
      if (filteredData.length === 0) {
        return 'N/A'
      }
      
      const totalPPG = filteredData.reduce((sum, stat) => {
        const ppg = parseFloat(stat.ppg)
        return sum + (isNaN(ppg) ? 0 : ppg)
      }, 0)
      
      const average = totalPPG / filteredData.length
      return average.toFixed(2)
    }
  }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>
